import type { HelpVideoProvider, HelpVideoType } from '@/types/help';

/**
 * Extract Vimeo privacy hash from a video URL (used for unlisted videos).
 * Matches both `vimeo.com/<id>/<hash>` path form and `?h=<hash>` query form.
 */
export function extractVimeoHash(videoUrl: string | null | undefined): string | null {
  if (!videoUrl) return null;
  // Path form: vimeo.com/<numericId>/<hash>
  const pathMatch = videoUrl.match(/vimeo\.com\/\d+\/([A-Za-z0-9]+)/);
  if (pathMatch) return pathMatch[1];
  // Query form: ?h=<hash> (player.vimeo.com/video/<id>?h=...)
  const queryMatch = videoUrl.match(/[?&]h=([A-Za-z0-9]+)/);
  if (queryMatch) return queryMatch[1];
  return null;
}

/**
 * Fetch a thumbnail URL via the provider's oEmbed API. Works for:
 *  - Vimeo single videos (public AND unlisted, when hash is supplied)
 *  - Vimeo showcases (playlists)
 *  - YouTube playlists
 *
 * Returns null if the lookup fails. Use this instead of `vumbnail.com` for
 * Vimeo (vumbnail returns HTTP 200 with a fake "error.jpg" image on failure).
 *
 * For YouTube single videos, prefer the static CDN URL
 * `https://img.youtube.com/vi/<id>/hqdefault.jpg` — no oEmbed needed.
 */
export async function fetchOembedThumbnail(params: {
  provider: HelpVideoProvider;
  type: HelpVideoType;
  videoId: string;
  vimeoHash?: string | null;
}): Promise<string | null> {
  const { provider, type, videoId, vimeoHash } = params;

  let endpoint: string;
  let resourceUrl: string;

  if (provider === 'YOUTUBE') {
    endpoint = 'https://www.youtube.com/oembed';
    resourceUrl =
      type === 'PLAYLIST'
        ? `https://www.youtube.com/playlist?list=${videoId}`
        : `https://www.youtube.com/watch?v=${videoId}`;
  } else {
    // VIMEO
    endpoint = 'https://vimeo.com/api/oembed.json';
    if (type === 'PLAYLIST') {
      resourceUrl = `https://vimeo.com/showcase/${videoId}`;
    } else {
      resourceUrl = vimeoHash
        ? `https://vimeo.com/${videoId}/${vimeoHash}`
        : `https://vimeo.com/${videoId}`;
    }
  }

  const r = await fetch(`${endpoint}?url=${encodeURIComponent(resourceUrl)}&format=json`);
  if (!r.ok) return null;
  const j = (await r.json()) as { thumbnail_url?: string };
  return j.thumbnail_url ?? null;
}
